// src/pages/BaoCaoNangSuat.jsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  AlertCircle,
  RefreshCw,
  Search,
  List,
  DollarSign,
  Package,
  Filter,
  Users,
  Plus,
  X,
  Calendar,
  Check,
  Upload,
  Download,
  BarChart3,
  PieChart as PieChartIcon
} from 'lucide-react';
import authUtils from '../utils/authUtils';
import { calculateProductivityReport } from '../payroll/core/productivity';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// === NEW: Excel export ===
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// === Recharts for charts ===
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const formatNumber = (val) => {
  const n = Number(val || 0);
  if (Number.isNaN(n)) return '0';
  return n.toLocaleString('vi-VN');
};

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('vi-VN');
};

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Generate unique ID
const generateUniqueId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  const timeBase = Date.now().toString(36).toUpperCase().slice(-4);
  result += timeBase.padStart(4, chars.charAt(Math.floor(Math.random() * chars.length)));
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Parse number safely
const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = parseInt(value, 10);
  return isNaN(num) ? 0 : Math.max(0, num);
};

// Lấy key tháng (YYYY-MM)
const getMonthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Convert date format - Hỗ trợ nhiều định dạng: YYYY-MM-DD, DD/MM/YYYY, Excel serial number
const convertDateFormat = (dateStr) => {
  if (!dateStr) return '';

  // yyyy-mm-dd (đã đúng định dạng)
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // dd/mm/yyyy (convert sang yyyy-mm-dd)
  if (typeof dateStr === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  // Excel serial number
  if (typeof dateStr === 'number') {
    const excelEpoch = new Date(Date.UTC(1900, 0, 1));
    const date = new Date(excelEpoch.getTime() + (dateStr - 2) * 24 * 60 * 60 * 1000);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Fallback: thử parse như date string thông thường
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch {
    /* ignore */
  }

  return '';
};

const BaoCaoNangSuat = () => {
  const [allData, setAllData] = useState({});
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [nhomList, setNhomList] = useState([]);
  const [filterTo, setFilterTo] = useState('');
  const [filterCongDoan, setFilterCongDoan] = useState('');
  const [filterNhanSu, setFilterNhanSu] = useState('');
  const [viewMode, setViewMode] = useState('detail'); // 'detail', 'monthly', or 'by-team'
  const [selectedMonth, setSelectedMonth] = useState(''); // Lọc theo tháng cho chế độ monthly
  const [selectedTeams, setSelectedTeams] = useState([]); // Lọc theo nhiều tổ cho chế độ by-team
  const [showTeamDropdown, setShowTeamDropdown] = useState(false); // Hiển thị dropdown chọn tổ

  // Ref cho dropdown để xử lý click outside
  const teamDropdownRef = useRef(null);

  // State cho modal nhập Pallet
  const [showPalletModal, setShowPalletModal] = useState(false);
  const [palletDates, setPalletDates] = useState([]); // Array of {date, entries: [{nghiepVu, to, khoiLuong}]}
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Import Excel preview & validation
  const [importPreview, setImportPreview] = useState(null); // Data after validation
  const [validationResults, setValidationResults] = useState(null); // {errors: [], warnings: [], isValid: bool, totalDays: number, totalRecords: number}

  // Thiết lập giá trị mặc định cho bộ lọc khi component mount
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Format YYYY-MM-DD cho fromDate và toDate
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Format YYYY-MM cho selectedMonth
    const formatMonth = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    };

    // Set giá trị mặc định
    setFromDate(formatDate(firstDayOfMonth));
    setToDate(formatDate(today));
    setSelectedMonth(formatMonth(today));
  }, []); // Chỉ chạy một lần khi component mount

  // Xử lý click outside để đóng dropdown chọn tổ
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target)) {
        setShowTeamDropdown(false);
      }
    };

    if (showTeamDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTeamDropdown]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Chỉ chế độ xem theo tháng mới bỏ lọc ngày (user chọn tháng từ dropdown)
      // Chế độ detail và by-team vẫn lọc theo fromDate/toDate
      const effectiveFromDate = viewMode === 'monthly' ? '' : fromDate;
      const effectiveToDate = viewMode === 'monthly' ? '' : toDate;

      const [nhapCt, toPbnsRaw, nhapBaoBi, xuatNhapKhoCt, congDoanDonGias, hieuChinhList] = await Promise.all([
        authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {}),
        authUtils.apiRequest('TO_PBNS', 'Find', {}),
        authUtils.apiRequest('NHAPBAOBI', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Find', {}),
        authUtils.apiRequest('CONGDOAN_DONGIA', 'Find', {}),
        authUtils.apiRequest('HIEU_CHINH_TIEN_NANG_SUAT', 'Find', {})
      ]);

      console.log('=== DEBUG DATA ===');
      console.log('NHAPBAOBI_CT records:', nhapCt.length);
      console.log('TO_PBNS records:', toPbnsRaw.length);
      console.log('NHAPBAOBI records:', nhapBaoBi.length);
      console.log('XUATNHAPKHO_CHITIET records:', xuatNhapKhoCt.length);
      console.log('CONGDOAN_DONGIA records:', congDoanDonGias.length);
      console.log('HIEU_CHINH_TIEN_NANG_SUAT records:', hieuChinhList.length);

      const { nhomList: uniqueNhoms, dataByNhom } = calculateProductivityReport({
        nhapCt, toPbnsRaw, nhapBaoBi, xuatNhapKhoCt, congDoanDonGias, hieuChinhList,
        fromDate: effectiveFromDate, toDate: effectiveToDate,
      });
      setNhomList(uniqueNhoms);

      if (!activeTab && uniqueNhoms.length > 0) {
        setActiveTab(uniqueNhoms[0]);
      }

      setAllData(dataByNhom);
    } catch (err) {
      console.error('Lỗi tải báo cáo năng suất:', err);
      setError(err.message || 'Lỗi khi tải báo cáo năng suất');
    } finally {
      setLoading(false);
    }
  };

  // Load template nghiệp vụ Pallet
  const loadPalletTemplate = async () => {
    try {
      const toPbnsData = await authUtils.apiRequest('TO_PBNS', 'Find', {});
      const palletCongDoan = toPbnsData.filter(item => item['NHÓM'] === 'Pallet');

      // Lấy danh sách nghiệp vụ unique với TỔ tương ứng
      const entriesMap = new Map();
      palletCongDoan.forEach(cd => {
        const nghiepVu = cd['TÊN CÔNG ĐOẠN'];
        const to = cd['TỔ'];
        if (nghiepVu && to && !entriesMap.has(nghiepVu)) {
          entriesMap.set(nghiepVu, {
            nghiepVu,
            to,
            khoiLuong: 0
          });
        }
      });

      return Array.from(entriesMap.values());
    } catch (error) {
      console.error('Error loading pallet template:', error);
      toast.error('Lỗi khi tải danh sách nghiệp vụ');
      return [];
    }
  };

  // Mở modal nhập Pallet - Khởi tạo với 1 ngày
  const handleOpenPalletModal = async () => {
    const template = await loadPalletTemplate();
    setPalletDates([
      {
        date: formatDateForInput(new Date()),
        entries: template
      }
    ]);
    setShowPalletModal(true);
  };

  // Đóng modal
  const handleClosePalletModal = () => {
    setShowPalletModal(false);
    setPalletDates([]);
  };

  // Thêm ngày mới
  const handleAddDate = async () => {
    const template = await loadPalletTemplate();
    setPalletDates(prev => [
      ...prev,
      {
        date: formatDateForInput(new Date()),
        entries: template
      }
    ]);
  };

  // Xóa ngày
  const handleRemoveDate = (index) => {
    setPalletDates(prev => prev.filter((_, i) => i !== index));
  };

  // Cập nhật ngày
  const handleUpdateDate = (index, newDate) => {
    setPalletDates(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, date: newDate } : item
      )
    );
  };

  // Cập nhật số lượng cho một nghiệp vụ trong một ngày
  const handleUpdateKhoiLuong = (dateIndex, nghiepVu, value) => {
    setPalletDates(prev =>
      prev.map((dateItem, i) =>
        i === dateIndex
          ? {
            ...dateItem,
            entries: dateItem.entries.map(entry =>
              entry.nghiepVu === nghiepVu
                ? { ...entry, khoiLuong: value }
                : entry
            )
          }
          : dateItem
      )
    );
  };

  // Lưu dữ liệu Pallet cho nhiều ngày
  const handleSavePallet = async () => {
    if (isSubmitting) return;

    // Validate
    if (palletDates.length === 0) {
      toast.error('Vui lòng thêm ít nhất một ngày');
      return;
    }

    // Kiểm tra có ngày nào không hợp lệ
    const invalidDate = palletDates.find(d => !d.date);
    if (invalidDate) {
      toast.error('Vui lòng chọn ngày cho tất cả các dòng');
      return;
    }

    try {
      setIsSubmitting(true);

      // Tạo mảng records để lưu từ tất cả các ngày
      const allRecordsToAdd = [];
      let totalValidEntries = 0;

      palletDates.forEach(dateItem => {
        // Lọc các entry có số lượng > 0
        const validEntries = dateItem.entries.filter(entry => parseNumber(entry.khoiLuong) > 0);

        validEntries.forEach(entry => {
          allRecordsToAdd.push({
            ID_CT: generateUniqueId(),
            IDPB_REF: '',
            IDCBB_REF: '',
            'NGÀY': dateItem.date,
            'TÊN HÀNG': 'Pallet',
            'NGHIỆP VỤ': entry.nghiepVu,
            'TỔ': entry.to,
            'KHỐI LƯỢNG (KG)': parseNumber(entry.khoiLuong)
          });
        });

        totalValidEntries += validEntries.length;
      });

      console.log('🔍 DEBUG - palletDates:', palletDates);
      console.log('🔍 DEBUG - allRecordsToAdd:', allRecordsToAdd);

      if (allRecordsToAdd.length === 0) {
        toast.error('Vui lòng nhập số lượng cho ít nhất một nghiệp vụ');
        return;
      }

      console.log('📤 Đang gửi lên server:', allRecordsToAdd);
      const response = await authUtils.apiRequest('NHAPBAOBI_CT', 'Add', { Rows: allRecordsToAdd });
      console.log('✅ Response từ server:', response);

      toast.success(`Thêm ${totalValidEntries} dòng dữ liệu Pallet cho ${palletDates.length} ngày thành công!`);
      handleClosePalletModal();
      await loadData(); // Reload data
    } catch (error) {
      console.error('❌ Error saving Pallet data:', error);
      toast.error('Có lỗi xảy ra khi lưu dữ liệu Pallet: ' + (error.message || 'Không xác định'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tải template Excel
  const handleDownloadTemplate = async () => {
    try {
      const template = await loadPalletTemplate();

      // Tạo data mẫu cho template
      const templateData = template.map((entry, idx) => ({
        'STT': idx + 1,
        'Ngày (DD/MM/YYYY)': '15/01/2025',
        'Nghiệp vụ': entry.nghiepVu,
        'Tổ': entry.to,
        'Số lượng': 0
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(templateData);

      // Set column widths
      ws['!cols'] = [
        { wch: 5 },  // STT
        { wch: 20 }, // Ngày
        { wch: 25 }, // Nghiệp vụ
        { wch: 15 }, // Tổ
        { wch: 12 }  // Số lượng
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Template Pallet');

      // Tạo sheet hướng dẫn
      const instructions = [
        ['HƯỚNG DẪN NHẬP DỮ LIỆU PALLET'],
        [],
        ['1. Cột "Ngày": Nhập theo định dạng DD/MM/YYYY (ví dụ: 15/01/2025)'],
        ['2. Cột "Nghiệp vụ" và "Tổ": KHÔNG ĐƯỢC THAY ĐỔI'],
        ['3. Cột "Số lượng": Nhập số lượng (bỏ trống hoặc 0 nếu không có)'],
        ['4. Có thể thêm nhiều dòng cho cùng một nghiệp vụ với ngày khác nhau'],
        ['5. Sau khi điền xong, lưu file và nhập vào hệ thống'],
        [],
        ['LƯU Ý:'],
        ['- Chỉ những dòng có Số lượng > 0 mới được lưu vào hệ thống'],
        ['- Nghiệp vụ và Tổ phải khớp với danh sách có sẵn'],
        ['- Ngày có thể nhập theo nhiều định dạng: DD/MM/YYYY, YYYY-MM-DD, hoặc để Excel tự format'],
      ];

      const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
      XLSX.utils.book_append_sheet(wb, wsInstructions, 'Hướng dẫn');

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `Template_Nhap_Pallet_${new Date().getTime()}.xlsx`);
      toast.success('Tải template thành công!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Lỗi khi tải template');
    }
  };

  // Nhập từ Excel - CHỈ PARSE VÀ VALIDATE, CHƯA LƯU
  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast.error('File Excel không có dữ liệu');
        return;
      }

      // Load template để validate
      const template = await loadPalletTemplate();
      const validNghiepVus = new Map(template.map(t => [t.nghiepVu, t.to]));

      // Validate và parse dữ liệu
      const errors = [];
      const warnings = [];
      const parsedDates = new Map();

      jsonData.forEach((row, index) => {
        const rowIndex = index + 2; // Dòng Excel (bỏ header)
        const rowErrors = [];

        const ngayRaw = row['Ngày (DD/MM/YYYY)'];
        const nghiepVu = row['Nghiệp vụ'];
        const to = row['Tổ'];
        const soLuong = parseNumber(row['Số lượng']);

        // Parse date - Hỗ trợ nhiều định dạng: YYYY-MM-DD, DD/MM/YYYY, Excel serial
        const dateStr = convertDateFormat(ngayRaw);

        // Validate
        if (!dateStr) {
          rowErrors.push(`Ngày không hợp lệ (${ngayRaw})`);
        }

        if (!nghiepVu) {
          rowErrors.push('Nghiệp vụ không được để trống');
        } else if (!validNghiepVus.has(nghiepVu)) {
          rowErrors.push(`Nghiệp vụ "${nghiepVu}" không hợp lệ`);
        }

        if (nghiepVu && validNghiepVus.has(nghiepVu) && validNghiepVus.get(nghiepVu) !== to) {
          rowErrors.push(`Tổ "${to}" không khớp với nghiệp vụ "${nghiepVu}"`);
        }

        if (soLuong < 0) {
          rowErrors.push('Số lượng không được âm');
        }

        // Nếu có lỗi, thêm vào danh sách errors
        if (rowErrors.length > 0) {
          errors.push(`Dòng ${rowIndex}: ${rowErrors.join('; ')}`);
        } else if (soLuong === 0) {
          // Warning nếu số lượng = 0
          warnings.push(`Dòng ${rowIndex}: Số lượng = 0 (sẽ bỏ qua)`);
        } else {
          // Valid row - Group by date
          if (!parsedDates.has(dateStr)) {
            parsedDates.set(dateStr, template.map(t => ({ ...t })));
          }

          const entries = parsedDates.get(dateStr);
          const entry = entries.find(e => e.nghiepVu === nghiepVu);
          if (entry) {
            entry.khoiLuong = (parseNumber(entry.khoiLuong) + soLuong).toString();
          }
        }
      });

      // Tính tổng số records
      let totalRecords = 0;
      parsedDates.forEach((entries) => {
        entries.forEach((entry) => {
          if (parseNumber(entry.khoiLuong) > 0) {
            totalRecords++;
          }
        });
      });

      // Convert to preview format
      const previewData = Array.from(parsedDates.entries())
        .map(([date, entries]) => ({
          date,
          entries: entries.filter(e => parseNumber(e.khoiLuong) > 0) // Chỉ hiện entries có số lượng > 0
        }))
        .filter(item => item.entries.length > 0); // Chỉ hiện ngày có ít nhất 1 entry

      // Set validation results
      setValidationResults({
        errors,
        warnings,
        isValid: errors.length === 0 && previewData.length > 0,
        totalDays: previewData.length,
        totalRecords
      });

      setImportPreview(previewData);

      if (errors.length === 0 && previewData.length > 0) {
        toast.success(`Đọc thành công ${previewData.length} ngày, ${totalRecords} bản ghi. Vui lòng kiểm tra và xác nhận!`);
      } else if (errors.length > 0) {
        toast.error(`Có ${errors.length} lỗi. Vui lòng kiểm tra và sửa file Excel!`);
      } else {
        toast.error('Không có dữ liệu hợp lệ để nhập');
      }
    } catch (error) {
      console.error('Error importing Excel:', error);
      toast.error('Lỗi khi đọc file Excel: ' + (error.message || 'Không xác định'));
      setImportPreview(null);
      setValidationResults(null);
    }

    // Reset input
    event.target.value = '';
  };

  // XÁC NHẬN IMPORT SAU KHI REVIEW
  const handleConfirmImport = async () => {
    if (!importPreview || !validationResults?.isValid) {
      toast.error('Dữ liệu không hợp lệ');
      return;
    }

    try {
      // Load template để có đầy đủ tất cả entries
      const template = await loadPalletTemplate();

      // Convert preview data sang palletDates format
      // Merge với template để đảm bảo có đầy đủ các entries (kể cả những entry = 0)
      const newPalletDates = importPreview.map(item => {
        // Tạo entries từ template
        const fullEntries = template.map(t => ({ ...t }));

        // Cập nhật giá trị từ import data
        item.entries.forEach(importEntry => {
          const entry = fullEntries.find(e => e.nghiepVu === importEntry.nghiepVu && e.to === importEntry.to);
          if (entry) {
            entry.khoiLuong = importEntry.khoiLuong;
          }
        });

        return {
          date: item.date,
          entries: fullEntries
        };
      });

      setPalletDates(newPalletDates);

      // Reset preview
      setImportPreview(null);
      setValidationResults(null);

      toast.success(`Đã nhập ${newPalletDates.length} ngày, ${validationResults.totalRecords} bản ghi vào form. Nhấn "Lưu" để lưu vào database!`);
    } catch (error) {
      console.error('Error confirming import:', error);
      toast.error('Lỗi khi xác nhận import');
    }
  };

  // HỦY IMPORT
  const handleCancelImport = () => {
    setImportPreview(null);
    setValidationResults(null);
    toast.info('Đã hủy import');
  };

  useEffect(() => {
    if (viewMode === 'monthly') {
      // Chế độ xem theo tháng: load toàn bộ, không cần fromDate/toDate
      loadData();
    } else if (fromDate && toDate) {
      // Chế độ detail và by-team: cần fromDate/toDate đã init
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, viewMode]);

  // Reset filter dropdowns when switching tabs (keep date filters)
  useEffect(() => {
    setFilterTo('');
    setFilterCongDoan('');
    setFilterNhanSu('');
  }, [activeTab]);

  const currentRows = useMemo(() => {
    return allData[activeTab] || [];
  }, [allData, activeTab]);

  const filteredRows = useMemo(
    () =>
      currentRows.filter((r) => {
        // Filter by search text
        const text = (
          formatDate(r.NGÀY) +
          ' ' +
          (r.TỔ || '') +
          ' ' +
          (r['CÔNG ĐOẠN'] || '') +
          ' ' +
          (r['NHÂN SỰ PHỤ TRÁCH'] || '') +
          ' ' +
          (r['LỊCH SỬ'] || '')
        ).toLowerCase();
        if (search && !text.includes(search.toLowerCase())) return false;

        // Filter by TỔ
        if (filterTo && r.TỔ !== filterTo) return false;

        // Filter by CÔNG ĐOẠN
        if (filterCongDoan && r['CÔNG ĐOẠN'] !== filterCongDoan) return false;

        // Filter by NHÂN SỰ PHỤ TRÁCH
        if (filterNhanSu && r['NHÂN SỰ PHỤ TRÁCH'] !== filterNhanSu) return false;

        return true;
      }),
    [currentRows, search, filterTo, filterCongDoan, filterNhanSu]
  );

  // Tổng hợp theo tháng - GỘP TẤT CẢ CÁC NHÓM
  const monthlyGroupedRows = useMemo(() => {
    if (viewMode !== 'monthly') return [];

    // Lấy TẤT CẢ dữ liệu từ tất cả các nhóm
    const allRowsFromAllGroups = [];
    Object.keys(allData).forEach((nhom) => {
      const rows = allData[nhom] || [];
      rows.forEach((row) => {
        allRowsFromAllGroups.push(row);
      });
    });

    const groupMap = new Map();

    allRowsFromAllGroups.forEach((row) => {
      const monthKey = getMonthKey(new Date(row.NGÀY)); // YYYY-MM

      // Lọc theo tháng nếu có chọn
      if (selectedMonth && monthKey !== selectedMonth) return;

      const to = row.TỔ || '';
      const congDoan = row['CÔNG ĐOẠN'] || '';
      const key = [monthKey, to, congDoan].join('|');

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          'THÁNG': monthKey,
          'TỔ': to,
          'CÔNG ĐOẠN': congDoan,
          'KHỐI LƯỢNG': 0,
          'SỐ LỒNG': 0,
          'ĐƠN VỊ TÍNH': row['ĐƠN VỊ TÍNH'] || '',
          'ĐƠN GIÁ': Number(row['ĐƠN GIÁ'] || 0),
          'THÀNH TIỀN': 0,
          'NHÂN SỰ PHỤ TRÁCH': row['NHÂN SỰ PHỤ TRÁCH'] || '',
        });
      }

      const item = groupMap.get(key);
      item['KHỐI LƯỢNG'] += Number(row['KHỐI LƯỢNG'] || 0);
      item['SỐ LỒNG'] += Number(row['SỐ LỒNG'] || 0);

      // Cập nhật đơn giá nếu có giá mới
      if (Number(row['ĐƠN GIÁ'] || 0) > 0) {
        item['ĐƠN GIÁ'] = Number(row['ĐƠN GIÁ'] || 0);
      }
    });

    // Tính thành tiền
    const result = Array.from(groupMap.values()).map((item) => ({
      ...item,
      'THÀNH TIỀN': item['KHỐI LƯỢNG'] * item['ĐƠN GIÁ'],
    }));

    // Sắp xếp theo tháng, tổ, công đoạn
    result.sort((a, b) => {
      if (a['THÁNG'] !== b['THÁNG']) return a['THÁNG'].localeCompare(b['THÁNG']);
      if (a['TỔ'] !== b['TỔ']) return a['TỔ'].localeCompare(b['TỔ']);
      return a['CÔNG ĐOẠN'].localeCompare(b['CÔNG ĐOẠN']);
    });

    return result;
  }, [allData, viewMode, selectedMonth]);

  // Lấy danh sách tháng có sẵn trong dữ liệu
  const availableMonths = useMemo(() => {
    if (viewMode !== 'monthly') return [];

    const monthsSet = new Set();
    Object.keys(allData).forEach((nhom) => {
      const rows = allData[nhom] || [];
      rows.forEach((row) => {
        const monthKey = getMonthKey(new Date(row.NGÀY));
        monthsSet.add(monthKey);
      });
    });

    return Array.from(monthsSet).sort();
  }, [allData, viewMode]);

  // ===== XỬ LÝ DỮ LIỆU THEO TỔ - GỘP TẤT CẢ CÁC NHÓM =====
  const processedDataByTeam = useMemo(() => {
    if (viewMode !== 'by-team') return [];

    // Lấy TẤT CẢ dữ liệu từ tất cả các nhóm
    const allRowsFromAllGroups = [];
    Object.keys(allData).forEach((nhom) => {
      const rows = allData[nhom] || [];
      rows.forEach((row) => {
        allRowsFromAllGroups.push({ ...row, NHÓM: nhom });
      });
    });

    // Nhóm dữ liệu theo TỔ
    const teamMap = new Map();

    allRowsFromAllGroups.forEach((row) => {
      const team = row.TỔ || 'Không xác định';
      const nhom = row.NHÓM;

      if (!teamMap.has(team)) {
        teamMap.set(team, {
          team: team,
          stages: new Map(), // Map theo công đoạn
          totalQuantity: 0,
          totalSoLong: 0,
          totalAmount: 0,
          staffSet: new Set(),
          records: [],
          // Map để tránh tính trùng khối lượng cho Bao bì anh/em (theo NGÀY|TỔ)
          uniqueToNgayForBaoBi: new Map()
        });
      }

      const teamData = teamMap.get(team);
      const congDoan = row['CÔNG ĐOẠN'] || 'Không xác định';
      const stageKey = congDoan;

      // Thêm công đoạn vào team
      if (!teamData.stages.has(stageKey)) {
        teamData.stages.set(stageKey, {
          stageName: congDoan,
          totalQuantity: 0,
          totalSoLong: 0,
          totalAmount: 0,
          staffSet: new Set(),
          records: []
        });
      }

      const stageData = teamData.stages.get(stageKey);

      // Cộng dồn số liệu
      const quantity = Number(row['KHỐI LƯỢNG'] || 0);
      const soLong = Number(row['SỐ LỒNG'] || 0);
      const amount = Number(row['THÀNH TIỀN'] || 0);

      stageData.totalQuantity += quantity;
      stageData.totalSoLong += soLong;
      stageData.totalAmount += amount;
      stageData.records.push(row);

      // Thêm nhân sự
      const staff = row['NHÂN SỰ PHỤ TRÁCH'];
      if (staff) {
        const staffList = staff.split(',').map(s => s.trim()).filter(Boolean);
        staffList.forEach(s => {
          stageData.staffSet.add(s);
          teamData.staffSet.add(s);
        });
      }

      // ===== XỬ LÝ KHỐI LƯỢNG THEO NHÓM =====
      // Với Bao bì anh và Bao bì em: chỉ tính khối lượng 1 lần cho mỗi TỔ+NGÀY
      if (nhom === 'Bao bì anh' || nhom === 'Bao bì em') {
        const dateKey = `${row.NGÀY}|${team}`;
        if (!teamData.uniqueToNgayForBaoBi.has(dateKey)) {
          // Lần đầu gặp TỔ+NGÀY này -> cộng khối lượng
          teamData.uniqueToNgayForBaoBi.set(dateKey, quantity);
          teamData.totalQuantity += quantity;
        }
        // Luôn cộng thành tiền (vì mỗi công đoạn có thành tiền riêng)
        teamData.totalAmount += amount;
      } else {
        // Các nhóm khác: tính bình thường
        teamData.totalQuantity += quantity;
        teamData.totalAmount += amount;
      }

      // Luôn cộng số lồng
      teamData.totalSoLong += soLong;

      teamData.records.push(row);
    });

    // Chuyển Map thành Array
    const result = Array.from(teamMap.values()).map(teamData => ({
      team: teamData.team,
      stages: Array.from(teamData.stages.values()).map(stage => ({
        stageName: stage.stageName,
        totalQuantity: stage.totalQuantity,
        totalSoLong: stage.totalSoLong,
        totalAmount: stage.totalAmount,
        staffCount: stage.staffSet.size,
        staffList: Array.from(stage.staffSet).sort(),
        records: stage.records
      })),
      totalQuantity: teamData.totalQuantity,
      totalSoLong: teamData.totalSoLong,
      totalAmount: teamData.totalAmount,
      staffCount: teamData.staffSet.size,
      stageCount: teamData.stages.size,
      recordCount: teamData.records.length
    }));

    // Sắp xếp theo tên tổ
    return result.sort((a, b) => a.team.localeCompare(b.team, 'vi'));
  }, [allData, viewMode]);

  // Lọc dữ liệu theo tổ
  const filteredDataByTeam = useMemo(() => {
    if (viewMode !== 'by-team') return [];

    let result = processedDataByTeam;

    // Lọc theo các tổ được chọn (chọn nhiều tổ)
    if (selectedTeams.length > 0) {
      result = result.filter(teamData => selectedTeams.includes(teamData.team));
    }

    return result;
  }, [processedDataByTeam, selectedTeams, viewMode]);

  // Lấy danh sách tổ có sẵn (cho dropdown filter)
  const availableTeams = useMemo(() => {
    if (viewMode !== 'by-team') return [];
    return processedDataByTeam.map(t => t.team).sort();
  }, [processedDataByTeam, viewMode]);

  // Tổng hợp thống kê cho chế độ by-team
  const summaryStatsByTeam = useMemo(() => {
    if (viewMode !== 'by-team') return null;

    const totalTeams = filteredDataByTeam.length;
    const totalQuantity = filteredDataByTeam.reduce((sum, t) => sum + t.totalQuantity, 0);
    const totalSoLong = filteredDataByTeam.reduce((sum, t) => sum + t.totalSoLong, 0);
    const totalAmount = filteredDataByTeam.reduce((sum, t) => sum + t.totalAmount, 0);

    const allStaff = new Set();
    filteredDataByTeam.forEach(teamData => {
      teamData.stages.forEach(stage => {
        stage.staffList.forEach(staff => allStaff.add(staff));
      });
    });

    return {
      totalTeams,
      totalQuantity,
      totalSoLong,
      totalAmount,
      totalStaff: allStaff.size
    };
  }, [filteredDataByTeam, viewMode]);

  // Thay thế phần tính totals hiện tại
  const totals = useMemo(() => {
    // Với Bao bì anh và Bao bì em: tính khối lượng theo TỔ + NGÀY (không trùng công đoạn)
    if (activeTab === 'Bao bì anh' || activeTab === 'Bao bì em') {
      const uniqueToNgay = new Map();

      filteredRows.forEach((r) => {
        const key = `${r.NGÀY}|${r.TỔ}`;
        if (!uniqueToNgay.has(key)) {
          uniqueToNgay.set(key, {
            khoiLuong: Number(r['KHỐI LƯỢNG'] || 0),
            thanhTien: 0
          });
        }
        // Cộng thành tiền của tất cả công đoạn
        uniqueToNgay.get(key).thanhTien += Number(r['THÀNH TIỀN'] || 0);
      });

      return Array.from(uniqueToNgay.values()).reduce(
        (acc, item) => {
          acc.khoiLuong += item.khoiLuong;
          acc.thanhTien += item.thanhTien;
          return acc;
        },
        { khoiLuong: 0, thanhTien: 0 }
      );
    }

    // Các nhóm khác: tính bình thường
    return filteredRows.reduce(
      (acc, r) => {
        acc.khoiLuong += Number(r['KHỐI LƯỢNG'] || 0);
        acc.thanhTien += Number(r['THÀNH TIỀN'] || 0);
        return acc;
      },
      { khoiLuong: 0, thanhTien: 0 }
    );
  }, [filteredRows, activeTab]);

  // Get unique values for filter dropdowns
  const uniqueTos = useMemo(() => {
    return [...new Set(currentRows.map(r => r.TỔ).filter(Boolean))].sort();
  }, [currentRows]);

  const uniqueCongDoans = useMemo(() => {
    return [...new Set(currentRows.map(r => r['CÔNG ĐOẠN']).filter(Boolean))].sort();
  }, [currentRows]);

  const uniqueNhanSus = useMemo(() => {
    return [...new Set(currentRows.map(r => r['NHÂN SỰ PHỤ TRÁCH']).filter(Boolean))].sort();
  }, [currentRows]);

  // === Màu sắc cho biểu đồ ===
  const CHART_COLORS = ['#ea580c', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  // === 1. Biểu đồ theo tháng cho nhóm 1 tổ (Cân, Tẩm, Thợ mũi A, E, Hàng tươi, Bãi, Bo lồng, Pallet) ===
  const monthlyChartDataForSingleTeam = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return [];

    // Nhóm theo tháng
    const monthMap = new Map();

    filteredRows.forEach(row => {
      const date = new Date(row['NGÀY']);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const quantity = Number(row['KHỐI LƯỢNG'] || 0);
      const amount = Number(row['THÀNH TIỀN'] || 0);

      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, {
          month: monthKey,
          monthLabel: `${date.getMonth() + 1}/${date.getFullYear()}`,
          totalQuantity: 0,
          totalAmount: 0,
          uniqueDates: new Set() // Để xử lý Bao bì anh/em
        });
      }

      const monthData = monthMap.get(monthKey);

      // Xử lý khối lượng theo nhóm
      if (activeTab === 'Bao bì anh' || activeTab === 'Bao bì em') {
        const dateKey = `${row['NGÀY']}|${row['TỔ']}`;
        if (!monthData.uniqueDates.has(dateKey)) {
          monthData.uniqueDates.add(dateKey);
          monthData.totalQuantity += quantity;
        }
      } else {
        monthData.totalQuantity += quantity;
      }

      monthData.totalAmount += amount;
    });

    // Chuyển Map thành mảng và sắp xếp theo tháng
    return Array.from(monthMap.values())
      .map(({ uniqueDates, ...rest }) => rest) // Loại bỏ uniqueDates
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredRows, activeTab]);

  // === 2. Biểu đồ hình tròn cho nhóm nhiều tổ (Bao bì anh, Bao bì em, Hàng khô) ===
  const pieChartDataByTeam = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return [];

    // Nhóm theo tổ
    const teamMap = new Map();

    filteredRows.forEach(row => {
      const team = row['TỔ'] || 'Không xác định';
      const quantity = Number(row['KHỐI LƯỢNG'] || 0);
      const amount = Number(row['THÀNH TIỀN'] || 0);
      const soLong = Number(row['SỐ LỒNG'] || 0);

      if (!teamMap.has(team)) {
        teamMap.set(team, {
          name: team,
          totalQuantity: 0,
          totalAmount: 0,
          totalSoLong: 0,
          uniqueDates: new Set() // Để xử lý Bao bì anh/em
        });
      }

      const teamData = teamMap.get(team);

      // Xử lý khối lượng theo nhóm
      if (activeTab === 'Bao bì anh' || activeTab === 'Bao bì em') {
        const dateKey = `${row['NGÀY']}|${team}`;
        if (!teamData.uniqueDates.has(dateKey)) {
          teamData.uniqueDates.add(dateKey);
          teamData.totalQuantity += quantity;
          teamData.totalSoLong += soLong;
        }
      } else {
        teamData.totalQuantity += quantity;
        teamData.totalSoLong += soLong;
      }

      teamData.totalAmount += amount;
    });

    // Chuyển Map thành mảng, tính phần trăm THEO KHỐI LƯỢNG và sắp xếp
    const teamArray = Array.from(teamMap.values()).map(({ uniqueDates, ...rest }) => rest);
    const totalQuantity = teamArray.reduce((sum, t) => sum + t.totalQuantity, 0); // ✅ Đổi từ totalAmount sang totalQuantity

    return teamArray
      .map(team => ({
        ...team,
        percentage: totalQuantity > 0 ? (team.totalQuantity / totalQuantity * 100).toFixed(1) : 0 // ✅ Tính % theo khối lượng
      }))
      .sort((a, b) => b.totalQuantity - a.totalQuantity); // ✅ Sắp xếp theo khối lượng
  }, [filteredRows, activeTab]);

  // === 3. Biểu đồ cột chồng theo tháng cho nhóm nhiều tổ ===
  const stackedBarChartDataByMonth = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return { data: [], teams: [] };

    const teams = [...new Set(filteredRows.map(r => r['TỔ']).filter(Boolean))].sort();
    const monthTeamMap = new Map();

    filteredRows.forEach(row => {
      const date = new Date(row['NGÀY']);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = `${date.getMonth() + 1}/${date.getFullYear()}`;
      const team = row['TỔ'] || 'Không xác định';
      const quantity = Number(row['KHỐI LƯỢNG'] || 0);
      const amount = Number(row['THÀNH TIỀN'] || 0);

      if (!monthTeamMap.has(monthKey)) {
        monthTeamMap.set(monthKey, {
          month: monthKey,
          monthLabel: monthLabel,
          teams: new Map(),
          uniqueDatesByTeam: new Map()
        });
      }

      const monthData = monthTeamMap.get(monthKey);

      if (!monthData.teams.has(team)) {
        monthData.teams.set(team, {
          quantity: 0,
          amount: 0
        });
      }

      if (activeTab === 'Bao bì anh' || activeTab === 'Bao bì em') {
        if (!monthData.uniqueDatesByTeam.has(team)) {
          monthData.uniqueDatesByTeam.set(team, new Set());
        }
        const dateKey = `${row['NGÀY']}|${team}`;
        if (!monthData.uniqueDatesByTeam.get(team).has(dateKey)) {
          monthData.uniqueDatesByTeam.get(team).add(dateKey);
          monthData.teams.get(team).quantity += quantity;
        }
      } else {
        monthData.teams.get(team).quantity += quantity;
      }

      monthData.teams.get(team).amount += amount;
    });

    const chartData = Array.from(monthTeamMap.values())
      .map(monthData => {
        const dataPoint = {
          month: monthData.monthLabel,
          monthKey: monthData.month
        };

        teams.forEach(team => {
          const teamData = monthData.teams.get(team);
          dataPoint[`${team}_quantity`] = teamData ? teamData.quantity : 0; // ✅ Lưu với suffix _quantity
          dataPoint[`${team}_amount`] = teamData ? teamData.amount : 0;
        });

        return dataPoint;
      })
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

    return { data: chartData, teams };
  }, [filteredRows, activeTab]);

  const resetFilters = () => {
    setFromDate('');
    setToDate('');
    setSearch('');
    setFilterTo('');
    setFilterCongDoan('');
    setFilterNhanSu('');
  };

  // ==== Export Excel - Xử lý 3 chế độ: detail, monthly, by-team ====
  const handleExportExcel = () => {
    try {
      // Kiểm tra dữ liệu theo chế độ
      if (viewMode === 'monthly') {
        if (!monthlyGroupedRows || monthlyGroupedRows.length === 0) {
          toast.warn('Không có dữ liệu để xuất Excel');
          return;
        }
      } else if (viewMode === 'by-team') {
        if (!filteredDataByTeam || filteredDataByTeam.length === 0) {
          toast.warn('Không có dữ liệu để xuất Excel');
          return;
        }
      } else {
        if (!filteredRows || filteredRows.length === 0) {
          toast.warn('Không có dữ liệu để xuất Excel');
          return;
        }
      }

      // ========== 1. Chuẩn bị Workbook ==========
      const wb = XLSX.utils.book_new();

      // ========== Nếu ở chế độ BÁO CÁO THEO TỔ ==========
      if (viewMode === 'by-team') {
        // Xuất báo cáo theo tổ
        const byTeamExportArray = [];

        filteredDataByTeam.forEach((teamData) => {
          teamData.stages.forEach((stage) => {
            byTeamExportArray.push({
              'Tổ': teamData.team,
              'Công đoạn': stage.stageName,
              'Tổng khối lượng': stage.totalQuantity,
              'Số nhân sự': stage.staffCount,
              'Tổng thành tiền': stage.totalAmount,
              'Danh sách nhân sự': stage.staffList.join(', '),
            });
          });
        });

        // Header
        const selectedTeamsText = selectedTeams.length > 0 ? selectedTeams.join(', ') : 'Tất cả';
        const headerByTeam = [
          ['BÁO CÁO NĂNG SUẤT THEO TỔ'],
          [
            'Từ ngày',
            fromDate ? formatDate(fromDate) : '',
            'Đến ngày',
            toDate ? formatDate(toDate) : '',
          ],
          [
            'Tổ được chọn',
            selectedTeamsText,
            'Tổng số tổ',
            filteredDataByTeam.length,
            'Tổng nhân sự',
            summaryStatsByTeam?.totalStaff || 0,
          ],
          [], // dòng trống
        ];

        const wsByTeam = XLSX.utils.aoa_to_sheet(headerByTeam);
        XLSX.utils.sheet_add_json(wsByTeam, byTeamExportArray, {
          origin: headerByTeam.length,
          skipHeader: false,
        });

        // Auto width
        if (byTeamExportArray.length > 0) {
          const colWidths = Object.keys(byTeamExportArray[0]).map((key) => ({
            wch: Math.max(
              key.length + 2,
              ...byTeamExportArray.map((row) => (row[key] ? row[key].toString().length + 2 : 10))
            ),
          }));
          wsByTeam['!cols'] = colWidths;
        }

        XLSX.utils.book_append_sheet(wb, wsByTeam, 'Báo cáo theo tổ');

        // Ghi file
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const fileName = `BaoCaoTheoTo_${selectedTeams.length > 0 ? selectedTeams.join('_') : 'TatCa'}_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);
        toast.success('Xuất Excel thành công!');
        return;
      }

      // ========== Nếu ở chế độ TỔNG HỢP THEO THÁNG ==========
      if (viewMode === 'monthly') {
        // Xuất bảng tổng hợp theo tháng
        const monthlyExportArray = monthlyGroupedRows.map((item, idx) => ({
          'STT': idx + 1,
          'Tháng': item['THÁNG'],
          'Tổ': item['TỔ'],
          'Công đoạn': item['CÔNG ĐOẠN'],
          'Khối lượng': item['KHỐI LƯỢNG'],
          'ĐVT': item['ĐƠN VỊ TÍNH'],
          'Đơn giá': item['ĐƠN GIÁ'],
          'Thành tiền': item['THÀNH TIỀN'],
          'Nhân sự phụ trách': item['NHÂN SỰ PHỤ TRÁCH'],
        }));

        // Header
        const headerMonthly = [
          ['BẢNG TỔNG HỢP THEO THÁNG'],
          [
            'Tháng',
            selectedMonth || 'Tất cả',
            'Tổng số dòng',
            monthlyGroupedRows.length,
          ],
          [], // dòng trống
        ];

        const wsMonthly = XLSX.utils.aoa_to_sheet(headerMonthly);
        XLSX.utils.sheet_add_json(wsMonthly, monthlyExportArray, {
          origin: headerMonthly.length,
          skipHeader: false,
        });

        // Auto width
        if (monthlyExportArray.length > 0) {
          const colWidths = Object.keys(monthlyExportArray[0]).map((key) => ({
            wch: Math.max(
              key.length + 2,
              ...monthlyExportArray.map((row) => (row[key] ? row[key].toString().length + 2 : 10))
            ),
          }));
          wsMonthly['!cols'] = colWidths;
        }

        XLSX.utils.book_append_sheet(wb, wsMonthly, 'Tổng hợp theo tháng');

        // Ghi file
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const fileName = `TongHopTheoThang_${selectedMonth || 'TatCa'}_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);
        toast.success('Xuất Excel thành công!');
        return;
      }

      // ==========================
      // SHEET 1: BẢNG TỔNG HỢP NĂNG SUẤT TỔ
      // ==========================
      // Gom theo: Họ và tên (NSPT), Tổ, Công đoạn
      const groupMap = new Map();

      filteredRows.forEach((row) => {
        const hoTen = row['NHÂN SỰ PHỤ TRÁCH'] || '';
        const to = row.TỔ || '';
        const congDoan = row['CÔNG ĐOẠN'] || '';
        const key = [hoTen, to, congDoan].join('|');

        if (!groupMap.has(key)) {
          groupMap.set(key, {
            hoTen,
            to,
            congDoan,
            khoiLuong: 0,
            donGia: Number(row['ĐƠN GIÁ'] || 0),
          });
        }

        const item = groupMap.get(key);
        item.khoiLuong += Number(row['KHỐI LƯỢNG'] || 0);

        // Nếu đơn giá trên các dòng khác nhau (không nên), ưu tiên giá mới nhất
        if (Number(row['ĐƠN GIÁ'] || 0) > 0) {
          item.donGia = Number(row['ĐƠN GIÁ'] || 0);
        }
      });

      const tongHopArray = Array.from(groupMap.values()).map((item, idx) => ({
        'STT': idx + 1,
        'Họ và tên': item.hoTen,
        'Tổ': item.to,
        'Công đoạn': item.congDoan,
        'Khối lượng': item.khoiLuong,
        'Đơn giá': item.donGia,
        'Thành tiền': item.khoiLuong * item.donGia,
      }));

      // Header cho sheet 1
      const headerTongHop = [
        ['BẢNG TỔNG HỢP NĂNG SUẤT TỔ'],
        [
          'Từ ngày',
          fromDate ? formatDate(fromDate) : '',
          'Đến ngày',
          toDate ? formatDate(toDate) : '',
          'Nhóm',
          activeTab || '',
        ],
        [], // dòng trống
      ];

      const wsTongHop = XLSX.utils.aoa_to_sheet(headerTongHop);
      XLSX.utils.sheet_add_json(wsTongHop, tongHopArray, {
        origin: headerTongHop.length,
        skipHeader: false,
      });

      // Auto width cho sheet 1
      if (tongHopArray.length > 0) {
        const colWidths1 = Object.keys(tongHopArray[0]).map((key) => ({
          wch: Math.max(
            key.length + 2,
            ...tongHopArray.map((row) => (row[key] ? row[key].toString().length + 2 : 10))
          ),
        }));
        wsTongHop['!cols'] = colWidths1;
      }

      XLSX.utils.book_append_sheet(wb, wsTongHop, 'Bảng tổng hợp');

      // ==========================
      // SHEET 2: BẢNG CHI TIẾT NĂNG SUẤT
      // ==========================

      // Xác định số ngày tối đa (thường là 30 hoặc 31)
      // Ở đây mình lấy 31 cho chắc, bạn có thể đổi thành 30 nếu muốn cố định
      const maxDay = 31;

      // group theo: Họ và tên, Tổ, Công đoạn, Tháng
      const detailMap = new Map();

      filteredRows.forEach((row) => {
        const hoTen = row['NHÂN SỰ PHỤ TRÁCH'] || '';
        const to = row.TỔ || '';
        const congDoan = row['CÔNG ĐOẠN'] || '';
        const d = new Date(row.NGÀY);
        if (isNaN(d.getTime())) return;

        const day = d.getDate(); // 1..31
        const monthKey = getMonthKey(d); // YYYY-MM

        const key = [hoTen, to, congDoan, monthKey].join('|');

        if (!detailMap.has(key)) {
          const initObj = {
            hoTen,
            to,
            congDoan,
            monthKey,
            tongKhoiLuong: 0,
          };
          // Tạo cột Ngày 1..maxDay = 0
          for (let i = 1; i <= maxDay; i += 1) {
            initObj[`Ngày ${i}`] = 0;
          }
          detailMap.set(key, initObj);
        }

        const item = detailMap.get(key);
        const kl = Number(row['KHỐI LƯỢNG'] || 0);
        item.tongKhoiLuong += kl;

        if (day >= 1 && day <= maxDay) {
          item[`Ngày ${day}`] += kl;
        }
      });

      const chiTietArray = Array.from(detailMap.values()).map((item, idx) => {
        const row = {
          'STT': idx + 1,
          'Họ và tên': item.hoTen,
          'Tổ': item.to,
          'Công đoạn': item.congDoan,
          'Tháng': item.monthKey,
          'Tổng khối lượng': item.tongKhoiLuong,
        };
        for (let i = 1; i <= maxDay; i += 1) {
          row[`Ngày ${i}`] = item[`Ngày ${i}`] || 0;
        }
        return row;
      });

      // Header cho sheet 2
      const headerChiTiet = [
        ['BẢNG CHI TIẾT NĂNG SUẤT'],
        [
          'Tháng',
          // nếu lọc 1 tháng thì có thể lấy từ fromDate; nếu nhiều tháng thì để trống
          fromDate ? getMonthKey(new Date(fromDate)) : '',
          'Tổ',
          '', // có thể để trống hoặc một tổ cụ thể nếu bạn lọc theo tổ
          'Nhóm',
          activeTab || '',
        ],
        [], // dòng trống
      ];

      const wsChiTiet = XLSX.utils.aoa_to_sheet(headerChiTiet);
      XLSX.utils.sheet_add_json(wsChiTiet, chiTietArray, {
        origin: headerChiTiet.length,
        skipHeader: false,
      });

      // Auto width cho sheet 2
      if (chiTietArray.length > 0) {
        const colWidths2 = Object.keys(chiTietArray[0]).map((key) => ({
          wch: Math.max(
            key.length + 2,
            ...chiTietArray.map((row) => (row[key] ? row[key].toString().length + 2 : 10))
          ),
        }));
        wsChiTiet['!cols'] = colWidths2;
      }

      XLSX.utils.book_append_sheet(wb, wsChiTiet, 'Bảng chi tiết');

      // ==========================
      // Ghi file
      // ==========================
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const fileName = `BaoCaoNangSuat_${activeTab || 'All'}_${formatDateForInput(
        fromDate || new Date()
      )}_${formatDateForInput(toDate || new Date())}.xlsx`;

      saveAs(blob, fileName);
    } catch (err) {
      console.error('Lỗi export Excel:', err);
      toast.error('Có lỗi khi xuất Excel');
    }
  };


  return (
    <div className="p-2 md:p-3 bg-gray-50 min-h-screen">
      <div className="mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-3 mb-4 border border-gray-100">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Báo cáo năng suất</h1>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {/* Nút bộ lọc và tìm kiếm - chỉ hiện ở chế độ detail */}
              {viewMode === 'detail' && (
                <>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm font-medium ${showFilters
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-blue-50'
                      }`}
                  >
                    <Filter className="w-4 h-4" />
                    Bộ lọc
                  </button>

                  <button
                    onClick={() => setShowSearch(!showSearch)}
                    className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm font-medium ${showSearch
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-blue-50'
                      }`}
                  >
                    <Search className="w-4 h-4" />
                    Tìm kiếm
                  </button>
                </>
              )}

              {/* Nút xem chi tiết */}
              <button
                onClick={() => setViewMode('detail')}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm font-medium ${viewMode === 'detail'
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <List className="w-4 h-4" />
                Xem chi tiết
              </button>

              {/* Nút tổng hợp theo tháng */}
              <button
                onClick={() => setViewMode('monthly')}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm font-medium ${viewMode === 'monthly'
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <Calendar className="w-4 h-4" />
                Xem theo tháng
              </button>

              {/* Nút báo cáo theo tổ */}
              <button
                onClick={() => setViewMode('by-team')}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm font-medium ${viewMode === 'by-team'
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <Users className="w-4 h-4" />
                Xem theo tổ
              </button>

              {/* Nút thêm dữ liệu Pallet - chỉ hiện khi tab Pallet được chọn và ở chế độ detail */}
              {viewMode === 'detail' && activeTab === 'Pallet' && (
                <button
                  onClick={handleOpenPalletModal}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  Nhập Pallet
                </button>
              )}

              {/* NEW: Nút xuất Excel */}
              <button
                onClick={handleExportExcel}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1.5 transition-colors shadow-sm"
              >
                <List className="w-4 h-4" />
                Xuất Excel
              </button>

              <button
                onClick={loadData}
                disabled={loading}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 transition-colors shadow-sm disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Làm mới
              </button>
            </div>
          </div>

          {/* Tabs - Ẩn khi ở chế độ tổng hợp theo tháng hoặc báo cáo theo tổ */}
          {viewMode === 'detail' && nhomList.length > 0 && (
            <div className="mb-4 border-b border-gray-200">
              <div className="flex flex-wrap gap-1.5">
                {nhomList.map((nhom) => (
                  <button
                    key={nhom}
                    onClick={() => setActiveTab(nhom)}
                    className={`px-3 py-1.5 font-medium text-sm rounded-t-lg transition-colors ${activeTab === nhom
                      ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {nhom}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-white bg-opacity-20">
                      {(allData[nhom] || []).length}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bộ lọc tháng - Chỉ hiện khi ở chế độ tổng hợp theo tháng */}
          {viewMode === 'monthly' && (
            <div className="mb-4 p-2 border-2 border-purple-200 rounded-lg bg-purple-50">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-purple-800">Chọn tháng:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="px-3 py-1.5 border border-purple-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 bg-white text-sm font-medium"
                >
                  <option value="">-- Tất cả các tháng --</option>
                  {availableMonths.map((month) => (
                    <option key={month} value={month}>
                      {month}
                    </option>
                  ))}
                </select>
                {selectedMonth && (
                  <button
                    onClick={() => setSelectedMonth('')}
                    className="px-2 py-1.5 text-purple-600 bg-white border border-purple-300 rounded-lg hover:bg-purple-100 text-sm flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Xóa lọc
                  </button>
                )}
                <div className="ml-auto text-sm text-purple-700 font-medium">
                  Hiển thị: <span className="font-bold">{monthlyGroupedRows.length}</span> dòng
                </div>
              </div>
            </div>
          )}

          {/* Bộ lọc tổ - Chỉ hiện khi ở chế độ báo cáo theo tổ */}
          {viewMode === 'by-team' && (
            <div className="mb-4 p-3 border border-orange-300 rounded-lg bg-gradient-to-br from-orange-50 to-white shadow-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className="block text-xs font-medium text-orange-700 mb-1">Từ ngày</label>
                  <input
                    type="date"
                    value={fromDate ? formatDateForInput(fromDate) : ''}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="p-1.5 border border-orange-200 rounded w-full focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-orange-700 mb-1">Đến ngày</label>
                  <input
                    type="date"
                    value={toDate ? formatDateForInput(toDate) : ''}
                    onChange={(e) => setToDate(e.target.value)}
                    className="p-1.5 border border-orange-200 rounded w-full focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-white text-sm"
                  />
                </div>

                <div className="relative" ref={teamDropdownRef}>
                  <label className="block text-xs font-medium text-orange-700 mb-1 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Chọn tổ
                  </label>

                  {/* Dropdown Button */}
                  <button
                    type="button"
                    onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                    className="w-full px-3 py-1.5 text-left bg-white border border-orange-200 rounded-lg hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">
                        {selectedTeams.length === 0 ? (
                          <span className="text-gray-400">-- Chọn tổ --</span>
                        ) : (
                          <span className="font-medium text-orange-700">
                            {selectedTeams.length} tổ đã chọn
                          </span>
                        )}
                      </span>
                      <svg
                        className={`w-5 h-5 text-orange-500 transition-transform ${showTeamDropdown ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Dropdown Menu */}
                  {showTeamDropdown && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-orange-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      <div className="p-1.5">
                        {/* Chọn tất cả */}
                        <label className="flex items-center gap-2 p-1.5 hover:bg-orange-50 rounded cursor-pointer transition-colors border-b border-orange-100 mb-1">
                          <input
                            type="checkbox"
                            checked={selectedTeams.length === availableTeams.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTeams([...availableTeams]);
                              } else {
                                setSelectedTeams([]);
                              }
                            }}
                            className="w-4 h-4 text-orange-600 border-orange-300 rounded focus:ring-orange-500"
                          />
                          <span className="text-sm font-semibold text-orange-700">Chọn tất cả</span>
                        </label>

                        {/* Danh sách tổ */}
                        {availableTeams.map((team) => (
                          <label
                            key={team}
                            className="flex items-center gap-2 p-1.5 hover:bg-orange-50 rounded cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTeams.includes(team)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTeams([...selectedTeams, team]);
                                } else {
                                  setSelectedTeams(selectedTeams.filter(t => t !== team));
                                }
                              }}
                              className="w-4 h-4 text-orange-600 border-orange-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700 font-medium">{team}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {(selectedTeams.length > 0 || fromDate || toDate) && (
                  <button
                    onClick={() => {
                      setSelectedTeams([]);
                      setFromDate('');
                      setToDate('');
                    }}
                    className="px-3 py-1.5 text-orange-700 bg-white border border-orange-300 rounded-lg hover:bg-orange-100 text-sm flex items-center gap-1.5 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Xóa lọc
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Filters - Chỉ hiện cho chế độ detail */}
          {showFilters && viewMode === 'detail' && (
            <div className="mb-4 p-3 border rounded-lg bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Từ ngày</label>
                  <input
                    type="date"
                    value={fromDate ? formatDateForInput(fromDate) : ''}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="p-1.5 border rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Đến ngày</label>
                  <input
                    type="date"
                    value={toDate ? formatDateForInput(toDate) : ''}
                    onChange={(e) => setToDate(e.target.value)}
                    className="p-1.5 border rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tổ</label>
                  <select
                    value={filterTo}
                    onChange={(e) => setFilterTo(e.target.value)}
                    className="p-1.5 border rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">-- Tất cả --</option>
                    {uniqueTos.map((to) => (
                      <option key={to} value={to}>
                        {to}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Công đoạn</label>
                  <select
                    value={filterCongDoan}
                    onChange={(e) => setFilterCongDoan(e.target.value)}
                    className="p-1.5 border rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">-- Tất cả --</option>
                    {uniqueCongDoans.map((cd) => (
                      <option key={cd} value={cd}>
                        {cd}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nhân sự phụ trách</label>
                  <select
                    value={filterNhanSu}
                    onChange={(e) => setFilterNhanSu(e.target.value)}
                    className="p-1.5 border rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">-- Tất cả --</option>
                    {uniqueNhanSus.map((ns) => (
                      <option key={ns} value={ns}>
                        {ns}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={resetFilters}
                    className="w-full px-3 py-1.5 text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    Xóa bộ lọc
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search - Chỉ hiện cho chế độ detail */}
          {showSearch && viewMode === 'detail' && (
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Tìm theo ngày, tổ, công đoạn, nhân sự, lịch sử..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-2 py-1.5 rounded-md text-sm mb-3">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Summary Cards */}
          <div className={`grid grid-cols-1 gap-3 mb-4 ${viewMode === 'by-team' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <div className={`p-3 rounded-lg border shadow-sm ${viewMode === 'monthly' ? 'bg-purple-50 border-purple-200' :
              viewMode === 'by-team' ? 'bg-orange-50 border-orange-200' :
                'bg-white border-gray-200'
              }`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`text-sm font-bold mb-1 ${viewMode === 'monthly' ? 'text-purple-600' :
                    viewMode === 'by-team' ? 'text-orange-600' :
                      'text-gray-500'
                    }`}>
                    {viewMode === 'by-team' ? 'Số tổ' : 'Số dòng'}
                  </p>
                  <h3 className={`text-2xl font-bold ${viewMode === 'monthly' ? 'text-purple-900' :
                    viewMode === 'by-team' ? 'text-orange-900' :
                      'text-gray-900'
                    }`}>
                    {viewMode === 'by-team' ? summaryStatsByTeam?.totalTeams || 0 :
                      viewMode === 'monthly' ? monthlyGroupedRows.length :
                        filteredRows.length}
                  </h3>
                </div>
                <div className={`p-1.5 rounded-lg ${viewMode === 'monthly' ? 'bg-purple-200' :
                  viewMode === 'by-team' ? 'bg-orange-200' :
                    'bg-blue-100'
                  }`}>
                  {viewMode === 'by-team' ? (
                    <Users className={`h-6 w-6 text-orange-700`} />
                  ) : (
                    <List className={`h-6 w-6 ${viewMode === 'monthly' ? 'text-purple-700' : 'text-blue-600'
                      }`} />
                  )}
                </div>
              </div>
            </div>

            <div className={`p-3 rounded-lg border shadow-sm ${viewMode === 'monthly' ? 'bg-purple-50 border-purple-200' :
              viewMode === 'by-team' ? 'bg-orange-50 border-orange-200' :
                'bg-white border-gray-200'
              }`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`text-sm font-bold mb-1 ${viewMode === 'monthly' ? 'text-purple-600' :
                    viewMode === 'by-team' ? 'text-orange-600' :
                      'text-gray-500'
                    }`}>Tổng khối lượng</p>
                  <h3 className={`text-2xl font-bold ${viewMode === 'monthly' ? 'text-purple-900' :
                    viewMode === 'by-team' ? 'text-orange-900' :
                      'text-gray-900'
                    }`}>
                    {viewMode === 'by-team' ? formatNumber(summaryStatsByTeam?.totalQuantity || 0) :
                      viewMode === 'monthly' ? formatNumber(monthlyGroupedRows.reduce((sum, r) => sum + Number(r['KHỐI LƯỢNG'] || 0), 0)) :
                        formatNumber(totals.khoiLuong)
                    }
                  </h3>
                </div>
                <div className={`p-1.5 rounded-lg ${viewMode === 'monthly' ? 'bg-purple-200' :
                  viewMode === 'by-team' ? 'bg-orange-200' :
                    'bg-green-100'
                  }`}>
                  <Package className={`h-6 w-6 ${viewMode === 'monthly' ? 'text-purple-700' :
                    viewMode === 'by-team' ? 'text-orange-700' :
                      'text-green-600'
                    }`} />
                </div>
              </div>
            </div>

            <div className={`p-3 rounded-lg border shadow-sm ${viewMode === 'monthly' ? 'bg-purple-50 border-purple-200' :
              viewMode === 'by-team' ? 'bg-orange-50 border-orange-200' :
                'bg-white border-gray-200'
              }`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`text-sm font-bold mb-1 ${viewMode === 'monthly' ? 'text-purple-600' :
                    viewMode === 'by-team' ? 'text-orange-600' :
                      'text-gray-500'
                    }`}>
                    {viewMode === 'by-team' ? 'Tổng nhân sự' : 'Tổng thành tiền'}
                  </p>
                  <h3 className={`text-2xl font-bold ${viewMode === 'monthly' ? 'text-purple-900' :
                    viewMode === 'by-team' ? 'text-orange-900' :
                      'text-gray-900'
                    }`}>
                    {viewMode === 'by-team' ? (
                      formatNumber(summaryStatsByTeam?.totalStaff || 0)
                    ) : (
                      new Intl.NumberFormat('vi-VN', {
                        style: 'currency',
                        currency: 'VND',
                      }).format(
                        viewMode === 'monthly'
                          ? monthlyGroupedRows.reduce((sum, r) => sum + Number(r['THÀNH TIỀN'] || 0), 0)
                          : totals.thanhTien
                      )
                    )}
                  </h3>
                </div>
                <div className={`p-1.5 rounded-lg ${viewMode === 'monthly' ? 'bg-purple-200' :
                  viewMode === 'by-team' ? 'bg-orange-200' :
                    'bg-purple-100'
                  }`}>
                  {viewMode === 'by-team' ? (
                    <Users className={`h-6 w-6 text-orange-700`} />
                  ) : (
                    <DollarSign className={`h-6 w-6 ${viewMode === 'monthly' ? 'text-purple-700' : 'text-purple-600'
                      }`} />
                  )}
                </div>
              </div>
            </div>

            {/* Card Tổng tiền NS - Chỉ hiện khi xem theo tổ */}
            {viewMode === 'by-team' && (
              <div className="p-3 rounded-lg border shadow-sm bg-orange-50 border-orange-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-bold mb-1 text-orange-600">
                      Tổng tiền NS
                    </p>
                    <h3 className="text-2xl font-bold text-orange-900">
                      {new Intl.NumberFormat('vi-VN', {
                        style: 'currency',
                        currency: 'VND',
                      }).format(summaryStatsByTeam?.totalAmount || 0)}
                    </h3>
                  </div>
                  <div className="p-1.5 rounded-lg bg-orange-200">
                    <DollarSign className="h-6 w-6 text-orange-700" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Biểu đồ - Chỉ hiện ở chế độ detail */}
          {viewMode === 'detail' && filteredRows && filteredRows.length > 0 && (
            <>
              {/* Nhóm có nhiều tổ: Bao bì anh, Bao bì em, Hàng khô */}
              {(activeTab === 'Bao bì anh' || activeTab === 'Bao bì em' || activeTab === 'Hàng khô') && uniqueTos.length > 1 && (
                <div className="space-y-4 mb-4">
                  {/* Biểu đồ hình tròn - Phân bố theo tổ */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <PieChartIcon className="w-5 h-5 text-orange-600" />
                        Phân bố năng suất theo tổ
                      </h3>
                      <p className="text-sm text-gray-600">Tỷ lệ phần trăm theo khối lượng</p> {/* ✅ Đổi text */}
                    </div>

                    {pieChartDataByTeam.length > 0 ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {/* Biểu đồ hình tròn */}
                        <ResponsiveContainer width="100%" height={350}>
                          <PieChart>
                            <Pie
                              data={pieChartDataByTeam}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percentage }) => `${name}: ${percentage}%`}
                              outerRadius={120}
                              fill="#8884d8"
                              dataKey="totalQuantity" // ✅ Đổi từ totalAmount sang totalQuantity
                            >
                              {pieChartDataByTeam.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-white p-2 border border-gray-300 rounded-lg shadow-lg">
                                      <p className="font-bold text-gray-800 mb-1">{data.name}</p>
                                      <div className="space-y-0.5 text-sm">
                                        <p className="text-gray-700">
                                          <span className="font-semibold">Khối lượng:</span> {formatNumber(data.totalQuantity)} {/* ✅ Đổi thứ tự */}
                                        </p>
                                        <p className="text-gray-700">
                                          <span className="font-semibold">Tỷ lệ:</span> {data.percentage}%
                                        </p>
                                        <p className="text-gray-700">
                                          <span className="font-semibold">Thành tiền:</span>{' '}
                                          {new Intl.NumberFormat('vi-VN', {
                                            style: 'currency',
                                            currency: 'VND'
                                          }).format(data.totalAmount)}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>

                        {/* Bảng thống kê */}
                        <div className="overflow-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border">Tổ</th>
                                {activeTab === 'Bao bì em' && (
                                  <th className="px-2 py-1.5 text-right font-semibold text-gray-700 border">Số lồng</th>
                                )}
                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 border">Khối lượng</th>
                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 border">Tỷ lệ (%)</th> {/* ✅ Đổi thứ tự cột */}
                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 border">Thành tiền</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pieChartDataByTeam.map((team, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-2 py-1.5 border">
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                                      ></div>
                                      {team.name}
                                    </div>
                                  </td>
                                  {activeTab === 'Bao bì em' && (
                                    <td className="px-2 py-1.5 text-right border">{formatNumber(team.totalSoLong)}</td>
                                  )}
                                  <td className="px-2 py-1.5 text-right border font-semibold">{formatNumber(team.totalQuantity)}</td>
                                  <td className="px-2 py-1.5 text-right border font-bold text-orange-600">{team.percentage}%</td> {/* ✅ Highlight % */}
                                  <td className="px-2 py-1.5 text-right border">
                                    {new Intl.NumberFormat('vi-VN', {
                                      style: 'currency',
                                      currency: 'VND'
                                    }).format(team.totalAmount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <PieChartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Không có dữ liệu để hiển thị</p>
                      </div>
                    )}
                  </div>

                  {/* Biểu đồ cột chồng theo tháng */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                        Năng suất theo tháng (Cột chồng theo tổ)
                      </h3>
                      <p className="text-sm text-gray-600">Khối lượng từng tổ trong mỗi tháng</p> {/* ✅ Đổi text */}
                    </div>

                    {stackedBarChartDataByMonth.data.length > 0 ? (
                      <ResponsiveContainer width="100%" height={600}>
                        <BarChart
                          data={stackedBarChartDataByMonth.data}
                          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="month"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 11 }}
                            stroke="#6b7280"
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            stroke="#6b7280"
                            tickFormatter={(value) => formatNumber(value)} // ✅ Đổi format
                            label={{ value: 'Khối lượng', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }} // ✅ Đổi label
                          />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                const total = payload.reduce((sum, p) => sum + (p.payload[`${p.dataKey}_quantity`] || 0), 0); // ✅ Tính tổng khối lượng
                                return (
                                  <div className="bg-white p-2 border border-gray-300 rounded-lg shadow-lg">
                                    <p className="font-bold text-gray-800 mb-1">{label}</p>
                                    <div className="space-y-0.5 text-sm">
                                      {payload.map((p, idx) => (
                                        <p key={idx} className="text-gray-700">
                                          <span className="font-semibold" style={{ color: p.fill }}>
                                            {p.dataKey}:
                                          </span>{' '}
                                          {formatNumber(p.payload[`${p.dataKey}_quantity`])} {/* ✅ Hiển thị khối lượng */}
                                        </p>
                                      ))}
                                      <p className="text-gray-900 font-bold pt-2 border-t mt-2">
                                        Tổng: {formatNumber(total)} {/* ✅ Tổng khối lượng */}
                                      </p>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend wrapperStyle={{ paddingTop: '10px' }} />
                          {stackedBarChartDataByMonth.teams.map((team, idx) => (
                            <Bar
                              key={team}
                              dataKey={`${team}_quantity`} // ✅ Đổi sang _quantity
                              stackId="a"
                              fill={CHART_COLORS[idx % CHART_COLORS.length]}
                              name={team}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12">
                        <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Không có dữ liệu để hiển thị</p>
                      </div>
                    )}
                  </div>

                </div>
              )}

              {/* Nhóm có 1 tổ: Cân, Tẩm, Thợ mũi A, E, Hàng tươi, Bãi, Bo lồng, Pallet */}
              {(activeTab === 'Cân' || activeTab === 'Tẩm' || activeTab === 'Thợ mũi A' ||
                activeTab === 'Thợ mũi E' || activeTab === 'Hàng tươi' || activeTab === 'Bãi' ||
                activeTab === 'Bo lồng' || activeTab === 'Pallet') && (
                  <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm mb-4">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-green-600" />
                        Năng suất theo tháng
                      </h3>
                      <p className="text-sm text-gray-600">Thành tiền và khối lượng theo từng tháng</p>
                    </div>

                    {monthlyChartDataForSingleTeam.length > 0 ? (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={monthlyChartDataForSingleTeam}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="monthLabel"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 11 }}
                            stroke="#6b7280"
                          />
                          <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 12 }}
                            stroke="#6b7280"
                            tickFormatter={(value) => `${formatNumber(value / 1000)}K`}
                            label={{ value: 'Thành tiền (₫)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                            stroke="#6b7280"
                            tickFormatter={(value) => formatNumber(value)}
                            label={{ value: 'Khối lượng', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                          />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-white p-4 border border-gray-300 rounded-lg shadow-lg">
                                    <p className="font-bold text-gray-800 mb-2">{label}</p>
                                    <div className="space-y-1 text-sm">
                                      <p className="text-gray-700">
                                        <span className="font-semibold">Thành tiền:</span>{' '}
                                        {new Intl.NumberFormat('vi-VN', {
                                          style: 'currency',
                                          currency: 'VND'
                                        }).format(payload[0]?.value || 0)}
                                      </p>
                                      <p className="text-gray-700">
                                        <span className="font-semibold">Khối lượng:</span> {formatNumber(payload[1]?.value || 0)}
                                      </p>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend wrapperStyle={{ paddingTop: '10px' }} />
                          <Bar
                            yAxisId="left"
                            dataKey="totalAmount"
                            fill="#10b981"
                            name="Thành tiền (₫)"
                            radius={[8, 8, 0, 0]}
                          />
                          <Bar
                            yAxisId="right"
                            dataKey="totalQuantity"
                            fill="#3b82f6"
                            name="Khối lượng"
                            radius={[8, 8, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12">
                        <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Không có dữ liệu để hiển thị</p>
                      </div>
                    )}
                  </div>
                )}
            </>
          )}

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-auto max-h-[70vh]">
              {viewMode === 'by-team' ? (
                // Hiển thị báo cáo theo tổ
                <ByTeamView data={filteredDataByTeam} />
              ) : viewMode === 'detail' ? (
                // Bảng chi tiết (gốc)
                <table className="min-w-full text-xs border-collapse">
                  <thead className="bg-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="border border-gray-200 px-2 py-1 text-left text-[11px] font-semibold text-gray-700">
                        NGÀY
                      </th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-[11px] font-semibold text-gray-700">
                        TỔ
                      </th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-[11px] font-semibold text-gray-700">
                        CÔNG ĐOẠN
                      </th>
                      {activeTab === 'Bao bì em' && (
                        <th className="border border-gray-200 px-2 py-1 text-right text-[11px] font-semibold text-gray-700">
                          SỐ LỒNG
                        </th>
                      )}
                      <th className="border border-gray-200 px-2 py-1 text-right text-[11px] font-semibold text-gray-700">
                        KHỐI LƯỢNG
                      </th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-[11px] font-semibold text-gray-700">
                        ĐƠN VỊ TÍNH
                      </th>
                      <th className="border border-gray-200 px-2 py-1 text-right text-[11px] font-semibold text-gray-700">
                        ĐƠN GIÁ
                      </th>
                      <th className="border border-gray-200 px-2 py-1 text-right text-[11px] font-semibold text-gray-700">
                        THÀNH TIỀN
                      </th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-[11px] font-semibold text-gray-700">
                        PP TÍNH NĂNG SUẤT
                      </th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-[11px] font-semibold text-gray-700">
                        NHÂN SỰ PHỤ TRÁCH
                      </th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-[11px] font-semibold text-gray-700 min-w-[260px]">
                        LỊCH SỬ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 && !loading && (
                      <tr>
                        <td colSpan={activeTab === 'Bao bì em' ? 11 : 10} className="text-center text-gray-500 py-6 italic">
                          Không có dữ liệu
                        </td>
                      </tr>
                    )}

                    {filteredRows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={
                          idx % 2 === 0
                            ? 'bg-white hover:bg-gray-50'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }
                      >
                        <td className="border border-gray-200 px-2 py-1">
                          {formatDate(r.NGÀY)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1">{r.TỔ}</td>
                        <td className="border border-gray-200 px-2 py-1">{r['CÔNG ĐOẠN']}</td>
                        {activeTab === 'Bao bì em' && (
                          <td className="border border-gray-200 px-2 py-1 text-right">
                            {formatNumber(r['SỐ LỒNG'])}
                          </td>
                        )}
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {formatNumber(r['KHỐI LƯỢNG'])}
                        </td>
                        <td className="border border-gray-200 px-2 py-1">{r['ĐƠN VỊ TÍNH']}</td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {formatNumber(r['ĐƠN GIÁ'])}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-emerald-700 font-semibold">
                          {formatNumber(r['THÀNH TIỀN'])}
                        </td>
                        <td className="border border-gray-200 px-2 py-1">
                          {r['PP TÍNH NĂNG SUẤT']}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-xs text-gray-700">
                          <div className="flex items-start gap-1">
                            <Users className="w-3 h-3 mt-[2px] text-gray-400" />
                            <span>{r['NHÂN SỰ PHỤ TRÁCH']}</span>
                          </div>
                        </td>
                        <td className="border border-gray-200 px-2 py-1 max-w-xl whitespace-nowrap overflow-hidden text-ellipsis text-[11px] text-gray-700">
                          {r['LỊCH SỬ']}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {filteredRows.length > 0 && (
                    <tfoot>
                      <tr className="bg-gray-900 text-white font-semibold">
                        <td
                          colSpan={3}
                          className="border border-gray-700 px-2 py-1 text-right text-[11px]"
                        >
                          TỔNG CỘNG:
                        </td>
                        <td className="border border-gray-700 px-2 py-1 text-right text-[11px]">
                          {formatNumber(totals.khoiLuong)}
                        </td>
                        <td className="border border-gray-700 px-2 py-1" />
                        <td className="border border-gray-700 px-2 py-1" />
                        <td className="border border-gray-700 px-2 py-1 text-right text-[11px]">
                          {formatNumber(totals.thanhTien)}
                        </td>
                        <td className="border border-gray-700 px-2 py-1" />
                        <td className="border border-gray-700 px-2 py-1" />
                        <td className="border border-gray-700 px-2 py-1" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              ) : (
                // Bảng tổng hợp theo tháng
                <table className="min-w-full text-xs border-collapse">
                  <thead className="bg-purple-100 sticky top-0 z-10">
                    <tr>
                      <th className="border border-purple-200 px-3 py-2 text-left text-xs font-bold text-purple-800">
                        THÁNG
                      </th>
                      <th className="border border-purple-200 px-3 py-2 text-left text-xs font-bold text-purple-800">
                        TỔ
                      </th>
                      <th className="border border-purple-200 px-3 py-2 text-left text-xs font-bold text-purple-800">
                        CÔNG ĐOẠN
                      </th>
                      <th className="border border-purple-200 px-3 py-2 text-right text-xs font-bold text-purple-800">
                        SỐ LỒNG
                      </th>
                      <th className="border border-purple-200 px-3 py-2 text-right text-xs font-bold text-purple-800">
                        KHỐI LƯỢNG
                      </th>
                      <th className="border border-purple-200 px-3 py-2 text-left text-xs font-bold text-purple-800">
                        ĐVT
                      </th>
                      <th className="border border-purple-200 px-3 py-2 text-right text-xs font-bold text-purple-800">
                        ĐƠN GIÁ
                      </th>
                      <th className="border border-purple-200 px-3 py-2 text-right text-xs font-bold text-purple-800">
                        THÀNH TIỀN
                      </th>
                      <th className="border border-purple-200 px-3 py-2 text-left text-xs font-bold text-purple-800">
                        NHÂN SỰ PHỤ TRÁCH
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyGroupedRows.length === 0 && !loading && (
                      <tr>
                        <td colSpan={9} className="text-center text-gray-500 py-6 italic">
                          Không có dữ liệu
                        </td>
                      </tr>
                    )}

                    {monthlyGroupedRows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={
                          idx % 2 === 0
                            ? 'bg-white hover:bg-purple-50'
                            : 'bg-purple-50 hover:bg-purple-100'
                        }
                      >
                        <td className="border border-gray-200 px-3 py-2 font-semibold text-purple-700">
                          {r['THÁNG']}
                        </td>
                        <td className="border border-gray-200 px-3 py-2">{r['TỔ']}</td>
                        <td className="border border-gray-200 px-3 py-2">{r['CÔNG ĐOẠN']}</td>
                        <td className="border border-gray-200 px-3 py-2 text-right">
                          {formatNumber(r['SỐ LỒNG'])}
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-right font-semibold">
                          {formatNumber(r['KHỐI LƯỢNG'])}
                        </td>
                        <td className="border border-gray-200 px-3 py-2">{r['ĐƠN VỊ TÍNH']}</td>
                        <td className="border border-gray-200 px-3 py-2 text-right">
                          {formatNumber(r['ĐƠN GIÁ'])}
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-right text-emerald-700 font-bold">
                          {formatNumber(r['THÀNH TIỀN'])}
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-xs text-gray-700">
                          <div className="flex items-start gap-1">
                            <Users className="w-3 h-3 mt-[2px] text-gray-400" />
                            <span>{r['NHÂN SỰ PHỤ TRÁCH']}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {monthlyGroupedRows.length > 0 && (
                    <tfoot>
                      <tr className="bg-purple-900 text-white font-bold">
                        <td
                          colSpan={3}
                          className="border border-purple-700 px-3 py-2 text-right text-xs"
                        >
                          TỔNG CỘNG:
                        </td>
                        <td className="border border-purple-700 px-3 py-2 text-right text-xs">
                          {formatNumber(
                            monthlyGroupedRows.reduce(
                              (sum, r) => sum + Number(r['SỐ LỒNG'] || 0),
                              0
                            )
                          )}
                        </td>
                        <td className="border border-purple-700 px-3 py-2 text-right text-xs">
                          {formatNumber(
                            monthlyGroupedRows.reduce(
                              (sum, r) => sum + Number(r['KHỐI LƯỢNG'] || 0),
                              0
                            )
                          )}
                        </td>
                        <td className="border border-purple-700 px-3 py-2" />
                        <td className="border border-purple-700 px-3 py-2" />
                        <td className="border border-purple-700 px-3 py-2 text-right text-xs">
                          {formatNumber(
                            monthlyGroupedRows.reduce(
                              (sum, r) => sum + Number(r['THÀNH TIỀN'] || 0),
                              0
                            )
                          )}
                        </td>
                        <td className="border border-purple-700 px-3 py-2" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal nhập Pallet */}
      {showPalletModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 px-3 py-2">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Package className="h-6 w-6 text-green-600" />
                  Nhập dữ liệu Pallet
                </h2>
                <button
                  onClick={handleClosePalletModal}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-3 py-3 space-y-3 overflow-y-auto flex-1">
              {/* Toolbar: Template + Import Excel + Thêm ngày */}
              <div className="flex justify-between items-center flex-wrap gap-2">
                <h3 className="text-lg font-semibold text-gray-800">
                  Danh sách ngày ({palletDates.length})
                </h3>
                <div className="flex gap-2">
                  {/* Tải template */}
                  <button
                    onClick={handleDownloadTemplate}
                    className="px-2 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1.5 text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Tải Template
                  </button>

                  {/* Nhập từ Excel */}
                  <label className="px-2 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1.5 text-sm font-medium cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Nhập Excel
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleImportExcel}
                      className="hidden"
                    />
                  </label>

                  {/* Thêm ngày thủ công */}
                  <button
                    onClick={handleAddDate}
                    className="px-2 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Thêm ngày
                  </button>
                </div>
              </div>

              {/* Validation Results - Hiển thị lỗi */}
              {validationResults && (
                <div className="space-y-3">
                  {validationResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm text-red-800 flex-1">
                          <p className="font-medium mb-2">
                            Lỗi ({validationResults.errors.length}):
                          </p>
                          <ul className="list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                            {validationResults.errors.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {validationResults.warnings.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm text-yellow-800 flex-1">
                          <p className="font-medium mb-2">
                            Cảnh báo ({validationResults.warnings.length}):
                          </p>
                          <ul className="list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                            {validationResults.warnings.map((warn, i) => (
                              <li key={i}>{warn}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {validationResults.isValid && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                      <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                        <Check className="w-5 h-5" />
                        Dữ liệu hợp lệ - Xem trước
                      </h3>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="bg-white p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="text-sm text-gray-600">Số ngày</span>
                          </div>
                          <div className="text-lg font-bold text-blue-600">
                            {validationResults.totalDays}
                          </div>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Package className="w-4 h-4 text-indigo-600" />
                            <span className="text-sm text-gray-600">Tổng bản ghi</span>
                          </div>
                          <div className="text-lg font-bold text-indigo-600">
                            {validationResults.totalRecords}
                          </div>
                        </div>
                      </div>

                      {/* Preview data */}
                      {importPreview && importPreview.length > 0 && (
                        <div className="border border-green-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                          <div className="bg-green-100 px-4 py-2 border-b border-green-200">
                            <h4 className="text-sm font-medium text-green-800">
                              Chi tiết dữ liệu import ({importPreview.length} ngày)
                            </h4>
                          </div>
                          <div className="p-3 space-y-3">
                            {importPreview.map((item, idx) => (
                              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-gray-800 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-blue-600" />
                                    {formatDate(item.date)}
                                  </span>
                                  <span className="text-xs text-gray-600">
                                    {item.entries.length} bản ghi
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  {item.entries.map((entry, entryIdx) => (
                                    <div
                                      key={entryIdx}
                                      className="flex justify-between items-center text-xs py-1 px-2 bg-gray-50 rounded"
                                    >
                                      <span className="text-gray-700">
                                        {entry.nghiepVu} - {entry.to}
                                      </span>
                                      <span className="font-semibold text-green-700">
                                        {formatNumber(entry.khoiLuong)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-green-200">
                        <button
                          onClick={handleCancelImport}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                        >
                          Hủy
                        </button>
                        <button
                          onClick={handleConfirmImport}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
                        >
                          <Check className="w-4 h-4" />
                          Xác nhận nhập ({validationResults.totalDays} ngày, {validationResults.totalRecords} bản ghi)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Danh sách các ngày */}
              {palletDates.map((dateItem, dateIndex) => (
                <div key={dateIndex} className="border-2 border-gray-300 rounded-lg p-2 space-y-2 bg-gray-50">
                  {/* Header của mỗi ngày */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ngày {dateIndex + 1} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <Calendar className="w-4 h-4 text-gray-500" />
                        </div>
                        <input
                          type="date"
                          value={dateItem.date}
                          onChange={(e) => handleUpdateDate(dateIndex, e.target.value)}
                          className="pl-10 p-2.5 border border-gray-300 rounded-lg w-full focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>
                    {palletDates.length > 1 && (
                      <button
                        onClick={() => handleRemoveDate(dateIndex)}
                        className="mt-6 px-2 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1.5 text-sm"
                      >
                        <X className="w-4 h-4" />
                        Xóa
                      </button>
                    )}
                  </div>

                  {/* Bảng nhập liệu cho ngày này */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-green-800 uppercase tracking-wider">
                            STT
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-green-800 uppercase tracking-wider">
                            Nghiệp vụ
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-green-800 uppercase tracking-wider">
                            Tổ
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-green-800 uppercase tracking-wider">
                            Số lượng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {dateItem.entries.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-3 py-4 text-center text-gray-500 italic text-xs">
                              Đang tải danh sách nghiệp vụ...
                            </td>
                          </tr>
                        ) : (
                          dateItem.entries.map((entry, entryIndex) => (
                            <tr key={entry.nghiepVu} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-xs text-gray-700">
                                {entryIndex + 1}
                              </td>
                              <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                {entry.nghiepVu}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-700">
                                {entry.to}
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  value={entry.khoiLuong}
                                  onChange={(e) => handleUpdateKhoiLuong(dateIndex, entry.nghiepVu, e.target.value)}
                                  className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 text-xs"
                                  placeholder="0"
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Thống kê cho ngày này */}
                  {dateItem.entries.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded p-2 text-xs">
                      <span className="text-gray-700">
                        <span className="font-semibold">Đã nhập:</span>{' '}
                        <span className="text-green-600 font-bold">
                          {dateItem.entries.filter(e => parseNumber(e.khoiLuong) > 0).length}
                        </span>
                        {' / '}
                        {dateItem.entries.length}
                      </span>
                    </div>
                  )}
                </div>
              ))}

              {/* Thống kê tổng */}
              {palletDates.length > 0 && (
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 text-sm">
                  <p className="text-blue-900 font-semibold">
                    <span>Tổng số ngày: {palletDates.length}</span>
                    {' | '}
                    <span>Tổng số dòng sẽ lưu: </span>
                    <span className="text-blue-700 font-bold">
                      {palletDates.reduce((total, dateItem) =>
                        total + dateItem.entries.filter(e => parseNumber(e.khoiLuong) > 0).length, 0
                      )}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-3 py-2 flex justify-end gap-2">
              <button
                onClick={handleClosePalletModal}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
              >
                Đóng
              </button>
              <button
                onClick={handleSavePallet}
                disabled={isSubmitting}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Đang lưu...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Lưu dữ liệu
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

// ===== COMPONENT HIỂN THỊ BÁO CÁO THEO TỔ =====
const ByTeamView = ({ data }) => {
  const [expandedTeams, setExpandedTeams] = React.useState(new Set());
  const [expandedStages, setExpandedStages] = React.useState(new Set());

  const toggleTeam = (team) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(team)) {
      newExpanded.delete(team);
    } else {
      newExpanded.add(team);
    }
    setExpandedTeams(newExpanded);
  };

  const toggleStage = (stageKey) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageKey)) {
      newExpanded.delete(stageKey);
    } else {
      newExpanded.add(stageKey);
    }
    setExpandedStages(newExpanded);
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <Users className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg font-medium">Không có dữ liệu báo cáo theo tổ</p>
        <p className="text-sm mt-1">Vui lòng chọn tổ và khoảng thời gian để xem báo cáo</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      {data.map((teamData, teamIdx) => {
        const isExpanded = expandedTeams.has(teamData.team);

        return (
          <div
            key={teamIdx}
            className="border border-orange-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all"
          >
            {/* Team Header - Clickable */}
            <div
              className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 cursor-pointer hover:from-orange-100 hover:to-orange-150 transition-colors border-b border-orange-200"
              onClick={() => toggleTeam(teamData.team)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500 rounded-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-orange-900">{teamData.team}</h3>
                    <p className="text-xs text-orange-600 mt-0.5">
                      {teamData.stageCount} công đoạn • {teamData.staffCount} nhân sự • {teamData.recordCount} dòng
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-orange-600 font-medium">Tổng thành tiền</p>
                    <p className="text-xl font-bold text-orange-700">
                      {new Intl.NumberFormat('vi-VN').format(teamData.totalAmount)} đ
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-orange-600 font-medium">Số lồng</p>
                    <p className="text-lg font-semibold text-orange-800">
                      {formatNumber(teamData.totalSoLong)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-orange-600 font-medium">Khối lượng</p>
                    <p className="text-lg font-semibold text-orange-800">
                      {formatNumber(teamData.totalQuantity)}
                    </p>
                  </div>
                  <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Details - Expandable */}
            {isExpanded && (
              <div className="bg-gray-50">
                <div className="grid grid-cols-1 gap-3 p-4">
                  {teamData.stages.map((stage, stageIdx) => {
                    const stageKey = `${teamData.team}-${stage.stageName}`;
                    const isStageExpanded = expandedStages.has(stageKey);

                    return (
                      <div
                        key={stageIdx}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                      >
                        {/* Stage Header - Clickable */}
                        <div
                          className="bg-gradient-to-r from-gray-50 to-white p-3 cursor-pointer hover:from-gray-100 hover:to-gray-50 transition-colors"
                          onClick={() => toggleStage(stageKey)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                {stage.stageName}
                              </h4>
                              <div className="flex items-center gap-4 mt-2 text-xs">
                                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {stage.staffCount} NS
                                </span>
                                <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded flex items-center gap-1">
                                  <Package className="w-3 h-3" />
                                  {formatNumber(stage.totalSoLong)} lồng
                                </span>
                                <span className="px-2 py-1 bg-green-50 text-green-700 rounded flex items-center gap-1">
                                  <Package className="w-3 h-3" />
                                  {formatNumber(stage.totalQuantity)}
                                </span>
                                <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded flex items-center gap-1">
                                  <DollarSign className="w-3 h-3" />
                                  {new Intl.NumberFormat('vi-VN').format(stage.totalAmount)} đ
                                </span>
                              </div>
                            </div>
                            <div className={`transform transition-transform ${isStageExpanded ? 'rotate-180' : ''}`}>
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>

                          {/* Staff List */}
                          {stage.staffList.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <div className="flex flex-wrap gap-1.5">
                                {stage.staffList.map((staff, staffIdx) => (
                                  <span
                                    key={staffIdx}
                                    className="px-2 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-medium"
                                  >
                                    {staff}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Records Table - Expandable */}
                        {isStageExpanded && (
                          <div className="overflow-x-auto border-t border-gray-200">
                            <table className="min-w-full text-xs">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 border-b border-gray-200">
                                    NGÀY
                                  </th>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 border-b border-gray-200">
                                    NHÓM
                                  </th>
                                  <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 border-b border-gray-200">
                                    SỐ LỒNG
                                  </th>
                                  <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 border-b border-gray-200">
                                    KHỐI LƯỢNG
                                  </th>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 border-b border-gray-200">
                                    ĐVT
                                  </th>
                                  <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 border-b border-gray-200">
                                    THÀNH TIỀN
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {stage.records.map((record, recIdx) => (
                                  <tr
                                    key={recIdx}
                                    className="hover:bg-orange-50 transition-colors"
                                  >
                                    <td className="px-3 py-2 text-[10px] text-gray-600">
                                      {formatDate(record.NGÀY)}
                                    </td>
                                    <td className="px-3 py-2 text-[10px] text-gray-700 font-medium">
                                      {record.NHÓM}
                                    </td>
                                    <td className="px-3 py-2 text-right text-[10px] text-gray-700">
                                      {formatNumber(record['SỐ LỒNG'])}
                                    </td>
                                    <td className="px-3 py-2 text-right text-[10px] text-gray-700 font-semibold">
                                      {formatNumber(record['KHỐI LƯỢNG'])}
                                    </td>
                                    <td className="px-3 py-2 text-[10px] text-gray-500">
                                      {record['ĐƠN VỊ TÍNH']}
                                    </td>
                                    <td className="px-3 py-2 text-right text-[10px] text-emerald-600 font-bold">
                                      {new Intl.NumberFormat('vi-VN').format(record['THÀNH TIỀN'])}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BaoCaoNangSuat;
